import { GridColDef, GridCellParams, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getDonors } from 'src/shared/services/donorService';
import { formatPersonalNumber } from 'src/shared/functions/formatStringToDate';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { Warning } from '@mui/icons-material';
import dayjs from 'dayjs';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import { AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { DonorResponse } from '../models/donor';

import { DonorTabValue } from './donorTabs';

type DonorDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchString?: string;
  statusFilter?: DonorTabValue;
};

interface DonorFilter {
  pageIndex: number;
  pageSize: number;
  sortCol: string;
  sortDir: string;
}

export const DonorDataGrid = (props: DonorDataGridProps) => {
  const { loadingCallback, searchString = undefined, statusFilter = 'all' } = props;
  const { t } = useTranslation(['gift', 'invoice']);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const [rows, setRows] = useState<DonorResponse[]>([]);

  const [donorFilter, setDonorFilter] = useLocalStorage<DonorFilter>(LocalStorageKeys.DonorSearchFilter, {
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
    sortCol: 'createdAt',
    sortDir: 'desc',
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: donorFilter?.sortCol ?? 'createdAt', sort: (donorFilter?.sortDir as 'asc' | 'desc') ?? 'desc' },
  ]);

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: donorFilter?.pageSize ?? DEFAULT_PAGE_SIZE,
    page: donorFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    setDonorFilter((prev) => ({ ...prev, pageIndex: newPageIndex }));
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    setDonorFilter((prev) => ({ ...prev, pageSize: newPageSize, pageIndex: 0 }));
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    loadingCallback(true);
    try {
      const result = await getDonors(
        data.page,
        data.pageSize,
        sortModel[0]?.field ?? 'createdAt',
        sortModel[0]?.sort ?? 'desc',
        searchString,
        statusFilter,
      );
      if (isMounted()) {
        const donorsResult = extendObjectWithId(result.data.results, 'id');
        updateData('totalRows', result.data.totalItems);
        if (isMounted()) setRows(donorsResult);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [data.page, data.pageSize, isMounted, loadingCallback, searchString, sortModel, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [data.page, data.pageSize, fetchData]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.DonorsDetail}/${params.row.id}`);
  };

  const translateFrequency = (frequency: number) => {
    switch (frequency) {
      case 1:
        return 'mån';
      case 3:
        return 'kvartal';
      case 12:
        return 'år';
      default:
        return '';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('gift:donorName'),
      flex: 1.2,
      minWidth: 180,
      sortable: true,
      renderCell: (params: GridCellParams) => <span>{params.row.name}</span>,
    },
    {
      field: 'personalIdentityNumber',
      headerName: t('gift:detailDonor.personalIdentityNumber'),
      width: 150,
      sortable: false,
      renderCell: (params: GridCellParams) => <span>{formatPersonalNumber(params.row.personalIdentityNumber)}</span>,
    },
    {
      field: 'subscription',
      headerName: t('gift:donorsDetailPage.donorSubscriptions'),
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const subs = params.row.subscriptions;
        if (!subs || subs.length === 0) return <span>—</span>;
        if (subs.length === 1) {
          const sub = subs[0];
          return (
            <span>
              <AmountWithCurrency amount={sub.amount} /> / {translateFrequency(sub.monthlyFrequency)}
            </span>
          );
        }
        return <span>{subs.length} st</span>;
      },
    },
    {
      field: 'nextDraw',
      headerName: 'Nästa dragning',
      width: 180,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const subs = params.row.subscriptions;
        const mandate = params.row.autogiroMandate;
        const hasActiveSub = subs && subs.some((s: any) => s.active);
        const hasApprovedMandate = mandate && mandate.status === AutogiroMandateStatusEnum.Approved;
        const hasPendingMandate = mandate && mandate.status === AutogiroMandateStatusEnum.PendingApproval;
        const isActionRequired = hasActiveSub && !hasApprovedMandate && !hasPendingMandate;
        const isWaiting = hasPendingMandate;

        if (!hasActiveSub) return <span>—</span>;

        // Beräkna nästa dragningsdatum från senaste faktura + frekvens
        const activeSubs = subs.filter((s: any) => s.active);
        const nextDates = activeSubs
          .map((s: any) => {
            if (!s.lastInvoiceCreated) return null;
            return dayjs(s.lastInvoiceCreated).add(s.monthlyFrequency, 'month');
          })
          .filter(Boolean);

        const nextDate = nextDates.length > 0 ? nextDates.sort((a: any, b: any) => a.diff(b))[0] : null;

        if (!nextDate) {
          if (isActionRequired || isWaiting) {
            const statusLabel = isActionRequired ? 'Åtgärd krävs' : 'Väntar';
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant='caption' sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                  Inväntar första...
                </Typography>
                <Tooltip title={statusLabel} arrow>
                  <Warning color='error' sx={{ fontSize: 18 }} />
                </Tooltip>
              </Box>
            );
          }
          return (
            <Typography variant='caption' color='text.secondary'>
              Inväntar första...
            </Typography>
          );
        }

        const dateStr = nextDate.format('YYYY-MM-DD');

        if (isActionRequired || isWaiting) {
          const statusLabel = isActionRequired ? 'Åtgärd krävs' : 'Väntar';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant='body2' sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                {dateStr}
              </Typography>
              <Tooltip title={statusLabel} arrow>
                <Warning color='error' sx={{ fontSize: 18 }} />
              </Tooltip>
            </Box>
          );
        }

        return <span>{dateStr}</span>;
      },
    },
    {
      field: 'email',
      headerName: t('gift:tableHeaders.email'),
      flex: 1.2,
      minWidth: 200,
      sortable: true,
      renderCell: (params: GridCellParams) => <span>{params.row.email ?? ''}</span>,
    },
    {
      field: 'createdAt',
      headerName: t('gift:tableHeaders.created'),
      width: 150,
      sortable: true,
      renderCell: (params: GridCellParams) => <span>{DateHelper.getDateTime(params.row.createdAt)}</span>,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 200,
      flex: 0.8,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const mandate = params.row.autogiroMandate;
        const subs = params.row.subscriptions;
        const hasActiveSub = subs && subs.some((s) => s.active);
        const hasApprovedMandate = mandate && mandate.status === AutogiroMandateStatusEnum.Approved;
        const hasPendingMandate = mandate && mandate.status === AutogiroMandateStatusEnum.PendingApproval;

        let label: string;
        let color: 'success' | 'error' | 'warning' | 'grey';

        if (hasApprovedMandate && hasActiveSub) {
          label = 'Aktiv';
          color = 'success';
        } else if (hasPendingMandate) {
          label = 'Väntar';
          color = 'success';
        } else if (!hasActiveSub && hasApprovedMandate) {
          label = 'Inaktiv';
          color = 'grey';
        } else {
          label = 'Åtgärd krävs';
          color = 'error';
        }

        return (
          <Stack direction='row' sx={{ '& .MuiChip-filled': { borderRadius: 1 }, '.MuiChip-label': { lineHeight: 'normal' } }}>
            <CustomChip label={label} color={color} size='small' />
          </Stack>
        );
      },
    },
  ];

  return (
    <Box ref={scrollRef}>
      <DataGrid<DonorResponse>
        columns={columns}
        rows={rows}
        autoHeight
        dataModel={data}
        checkboxSelection={false}
        onRowClick={onRowClick}
        disableSelectionOnClick
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
            setDonorFilter((prev) => ({ ...prev, sortCol: newSortModel[0].field, sortDir: newSortModel[0].sort ?? 'desc' }));
          } else {
            const newSort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
            setSortModel([{ field: sortModel[0].field, sort: newSort }]);
            setDonorFilter((prev) => ({ ...prev, sortDir: newSort }));
          }
        }}
        onSelectionModelChange={() => ConsoleHelper.log()}
        selectionModel={[]}
      />
    </Box>
  );
};
